import { create } from 'zustand';

// Define types for our audio items
export interface AudioItem {
  id: string;
  name: string;
  singer: string;
  cover: string;
  musicSrc: string;
}

interface MusicPlayerState {
  // Playlist state
  audioLists: AudioItem[];
  isPlayerVisible: boolean;
  
  // Playback state
  isPlaying: boolean;
  isPaused: boolean;
  
  // Audio analysis
  audioContext: AudioContext | null;
  audioElement: HTMLAudioElement | null;
  // Keep a reference to the MediaElementAudioSourceNode to avoid duplicate creation
  mediaSource: MediaElementAudioSourceNode | null;

  // Actions
  setAudioLists: (audioLists: AudioItem[]) => void;
  addSongsToPlaylist: (songs: AudioItem[]) => void;
  addAlbumToPlaylist: (albumId: string, songs: AudioItem[]) => void;
  setPlayerVisible: (visible: boolean) => void;
  
  // Playback actions
  setPlayingState: (isPlaying: boolean) => void;
  setPausedState: (isPaused: boolean) => void;
}

// Create a store with Zustand (simple state management)
export const useMusicPlayerStore = create<MusicPlayerState>((set, get) => ({
  audioLists: [],
  isPlayerVisible: false,
  isPlaying: false,
  isPaused: true,
  audioContext: null,
  audioElement: null,
  mediaSource: null,
  
  // Set the entire playlist - only update if different
  setAudioLists: (audioLists: AudioItem[]) => {
    console.debug("[MusicPlayerService] Setting audio lists:", audioLists.length);
    // Don't update if the lists are identical (by ID)
    const currentIds = get().audioLists.map((item: { id: string; }) => item.id).sort().join(',');
    const newIds = audioLists.map((item: { id: string; }) => item.id).sort().join(',');
    
    if (currentIds !== newIds) {
      console.debug("[MusicPlayerService] Updating audio lists (different IDs)");
      set({ audioLists });
    } else {
      console.debug("[MusicPlayerService] No update needed (same IDs)");
    }
  },
  
  // Add songs to playlist without duplicates
  addSongsToPlaylist: (songs: AudioItem[]) => {
    console.debug("[MusicPlayerService] Adding songs to playlist:", songs.length);
    const { audioLists } = get();
    
    // Filter out songs that already exist in the playlist
    const newSongs = songs.filter(newSong => 
      !audioLists.some((existingSong: { id: string; }) => existingSong.id === newSong.id)
    );
    
    // Only update if there are new songs to add
    if (newSongs.length > 0) {
      console.debug("[MusicPlayerService] Adding new songs to playlist:", newSongs.length);
      set({ 
        audioLists: [...audioLists, ...newSongs],
        isPlayerVisible: true 
      });
    } else {
      console.debug("[MusicPlayerService] No new songs to add (duplicates filtered)");
    }
  },
  
  // Add album songs to playlist
  addAlbumToPlaylist: (albumId: string, songs: AudioItem[]) => {
    console.debug("[MusicPlayerService] Adding album to playlist:", albumId, songs.length, "songs");
    get().addSongsToPlaylist(songs);
  },
  
  // Set player visibility
  setPlayerVisible: (visible: boolean) => {
    console.debug("[MusicPlayerService] Setting player visibility:", visible);
    set({ isPlayerVisible: visible });
  },

  // Set playing state
  setPlayingState: (isPlaying: boolean) => {
    console.debug("[MusicPlayerService] Setting playing state:", isPlaying);
    set({ isPlaying, isPaused: !isPlaying });
    // Ensure main is visible when playback stops
    if (!isPlaying) {
      set({ isPlayerVisible: true });
    }
  },

  // Set paused state
  setPausedState: (isPaused: boolean) => {
    console.debug("[MusicPlayerService] Setting paused state:", isPaused);
    set({ isPaused, isPlaying: !isPaused });
    if (isPaused) {
      set({ isPlayerVisible: true });
    }
  },
}));

export const defaultPlayerOptions = {
    audioLists: [],
    toggleMode: false,
    bounds: 'main',
    preload: 'none' as const,
    clearPriorAudioLists: false,
    autoPlay: false,
    defaultPlayIndex: 0,
    defaultPlayMode: "order",
    theme: "dark",
    mode: "full",
    audioInfo: "test",
    glassBg: true,
    remember: true,
    responsive: false,
    sortableOptions: { swap: true, animation: 100, swapClass: 'audio-lists-panel-sortable-highlight-bg' },
    showMiniProcessBar: true,
    showMiniModeCover: true,
    showProgressBar: true,
    showMediaSession: true,
    showDownload: false,
    showThemeSwitch: false,
    showReload: false,
    defaultPosition: {
        top: 60,
        right: 0,
    },
    showPlay: true,
    showPlayMode: true,
    showLyric: false,
    showDestroy: false,
    quietUpdate: true,
    volumeFade: { fadeIn: 1000, fadeOut: 1000 },
    spaceBar: true,
    className: ""
};

